// Add or edit an app in the catalogue (spec/14 §6; F-CAT-01): a non-game title — a browser, voice chat, a
// music player, a game launcher — that venues add to their library and players open from the kiosk's Apps
// screen. A short stepper rather than one long form (spec/adr/0027, BRAND.md §6):
//
//   Name and category → Launch → For players → Icon → Confirm      (adding)
//   Name and category → Launch → For players → Confirm             (editing — the icon lives under Media)
//
// "For players" (F-CAT-09, ADR-0045) is how a station installs the app into its player account when a venue turns
// Install for players on — a Store package or the vendor's installer — and where the app keeps a player's sign-in
// (`ProvisioningFields.tsx`). Only HQ writes it.
//
// "Launch" is the same recipe editor a game's launch recipes use (`RecipeDraftsEditor`), plus the detection rules
// a station recognises the app by (`DetectionFields`, shared with a game's Detection dialog; spec/10 §7.3).
// "Icon" takes a square PNG or WebP, uploaded once the app exists (`icon-upload.ts`); a SteamGridDB icon can be
// picked afterwards under Media. Nothing is written until Confirm.

import type { AppCategory, Game, LaunchRecipe } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, SelectField, Stepper, TextField, useToast } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';

import { useApi } from '../../api/client';
import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { Notice } from '../../ui';

import { formatIconCap, ICON_MAX_BYTES, iconFileProblem, uploadAppIcon } from './icon-upload';
import { DetectionFields, detectionDraftOf, parseProcessNames, toDetection, type DetectionDraft } from './DetectionDialog';
import { draftFrom, RecipeDraftsEditor, toRecipe, type RecipeDraft } from './LaunchRecipesDialog';
import {
  ProvisioningFields,
  provisioningDraftOf,
  signOutDraftsOf,
  toProvisioning,
  toSignOutPaths,
  type ProvisioningDraft,
  type SignOutDraft,
} from './ProvisioningFields';
import { catalogueKeys, useCreateApp, useUpdateApp } from './queries';
import { UploadDrop } from './UploadDrop';

/** The kiosk categories an app may suggest (`AppCategory`), in the picker's order. */
export const APP_CATEGORIES: readonly AppCategory[] = ['apps', 'browsers', 'voice', 'media', 'tools'];

interface AppDraft {
  name: string;
  summary: string;
  category: AppCategory;
  recipes: RecipeDraft[];
  detection: DetectionDraft;
  provisioning: ProvisioningDraft;
  signOut: SignOutDraft[];
  icon: File | null;
}

function draftOf(app: Game | undefined): AppDraft {
  return {
    name: app?.name ?? '',
    summary: app?.summary ?? '',
    category: (APP_CATEGORIES as readonly string[]).includes(app?.suggestedCategory ?? '') ? (app?.suggestedCategory as AppCategory) : 'apps',
    recipes: app === undefined ? [] : app.launchRecipes.map(draftFrom),
    detection: detectionDraftOf(app?.detection),
    provisioning: provisioningDraftOf(app),
    signOut: signOutDraftsOf(app),
    icon: null,
  };
}

export function AppDialog({ app, open, onOpenChange }: { readonly app?: Game | undefined; readonly open: boolean; readonly onOpenChange: (open: boolean) => void }): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg" dirty="auto" title={app === undefined ? t('catalogue.apps.sheet.createTitle') : t('catalogue.apps.sheet.editTitle')} description={app?.name}>
      {open ? <AppFlow app={app} onDone={() => onOpenChange(false)} /> : null}
    </Dialog>
  );
}

type StepId = 'name' | 'launch' | 'players' | 'icon' | 'confirm';

function AppFlow({ app, onDone }: { readonly app: Game | undefined; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const api = useApi();
  const queryClient = useQueryClient();
  const create = useCreateApp();
  const update = useUpdateApp();
  const creating = app === undefined;
  const steps: readonly StepId[] = creating ? ['name', 'launch', 'players', 'icon', 'confirm'] : ['name', 'launch', 'players', 'confirm'];
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<AppDraft>(() => draftOf(app));
  const [error, setError] = useState<string | undefined>(undefined);
  const [iconError, setIconError] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const current = steps[step] ?? 'name';
  const cap = formatIconCap(ICON_MAX_BYTES);
  const categories = useMemo(() => APP_CATEGORIES.map((category) => ({ value: category, label: t(`catalogue.apps.category.${category}`) })), [t]);

  const recipes = (): LaunchRecipe[] | null => {
    const out: LaunchRecipe[] = [];
    for (const recipe of draft.recipes) {
      const parsed = toRecipe(recipe);
      if (parsed === null) return null;
      out.push(parsed);
    }
    return out;
  };

  const next = () => {
    if (current === 'name' && draft.name.trim() === '') {
      setError(t('catalogue.apps.sheet.nameRequired'));
      return;
    }
    if (current === 'launch' && recipes() === null) {
      setError(t('catalogue.launch.incomplete'));
      return;
    }
    if (current === 'players') {
      const recipe = toProvisioning(draft.provisioning);
      if (!recipe.ok) {
        setError(t(`catalogue.apps.players.problem.${recipe.problem}`));
        return;
      }
      const signOut = toSignOutPaths(draft.signOut);
      if (!signOut.ok) {
        setError(t('catalogue.apps.players.problem.signOutPath'));
        return;
      }
    }
    setError(undefined);
    setStep((index) => Math.min(index + 1, steps.length - 1));
  };

  const failure = (err: unknown) =>
    setError(
      problemCode(err) === PROBLEM.conflict
        ? t('catalogue.apps.sheet.nameTaken')
        : problemCode(err) === PROBLEM.validationFailed
          ? (problems.view(err).fields[0]?.message ?? t('catalogue.apps.players.problem.server'))
          : problems.text(err, { fallback: t('catalogue.apps.sheet.failed') }),
    );

  const submit = async () => {
    const launchRecipes = recipes() ?? [];
    const detection = toDetection(draft.detection);
    const summary = draft.summary.trim() === '' ? null : draft.summary.trim();
    // Checked on leaving "For players"; held here so a jump back from the stepper cannot send a half-written recipe.
    const recipe = toProvisioning(draft.provisioning);
    const signOut = toSignOutPaths(draft.signOut);
    if (!recipe.ok || !signOut.ok) {
      setError(t(`catalogue.apps.players.problem.${recipe.ok ? 'signOutPath' : recipe.problem}`));
      return;
    }
    const players = { provisioning: recipe.provisioning, signOutPaths: signOut.ok ? signOut.paths : [] };
    setError(undefined);
    setSaving(true);
    try {
      if (app !== undefined) {
        const saved = await update.mutateAsync({
          id: app.id,
          body: { name: draft.name.trim(), summary, suggestedCategory: draft.category, launchRecipes, detection, ...players },
        });
        show({ tone: 'ok', title: t('catalogue.apps.sheet.saved', { app: saved.name }) });
        onDone();
        return;
      }
      const created = await create.mutateAsync({ name: draft.name.trim(), summary, suggestedCategory: draft.category, launchRecipes, detection, ...players });
      if (draft.icon !== null) {
        try {
          await uploadAppIcon(api, created.id, draft.icon);
          void queryClient.invalidateQueries({ queryKey: catalogueKeys.games });
        } catch {
          // The app exists; only its icon is missing. Say so, and close — the icon can be set under Media.
          show({ tone: 'warn', title: t('catalogue.apps.sheet.iconFailed', { app: created.name }) });
          onDone();
          return;
        }
      }
      show({ tone: 'ok', title: t('catalogue.apps.sheet.created', { app: created.name }) });
      onDone();
    } catch (err) {
      failure(err);
    } finally {
      setSaving(false);
    }
  };

  const label = (id: StepId) =>
    t(
      id === 'name'
        ? 'catalogue.apps.sheet.stepName'
        : id === 'launch'
          ? 'catalogue.apps.sheet.stepLaunch'
          : id === 'players'
            ? 'catalogue.apps.sheet.stepPlayers'
            : id === 'icon'
              ? 'catalogue.apps.sheet.stepIcon'
              : 'catalogue.apps.sheet.stepConfirm',
    );

  return (
    <div className="console-stepper-flow">
      <Stepper current={step} onStepSelect={setStep} steps={steps.map((id) => ({ id, label: label(id) }))} />
      <div className="console-form">
        {error !== undefined ? (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        ) : null}

        {current === 'name' ? (
          <>
            <TextField label={t('catalogue.apps.sheet.name')} description={t('catalogue.apps.sheet.nameHint')} required value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
            <TextField label={t('catalogue.apps.sheet.summary')} showOptional value={draft.summary} onChange={(event) => setDraft({ ...draft, summary: event.target.value })} />
            <SelectField
              label={t('catalogue.apps.sheet.category')}
              description={t('catalogue.apps.sheet.categoryHint')}
              options={categories}
              value={draft.category}
              onValueChange={(value) => setDraft({ ...draft, category: value as AppCategory })}
            />
          </>
        ) : null}

        {current === 'launch' ? (
          <>
            <p className="console-muted">{t('catalogue.apps.sheet.launchIntro')}</p>
            <RecipeDraftsEditor drafts={draft.recipes} onChange={(recipes) => setDraft({ ...draft, recipes })} />
            <DetectionFields
              draft={draft.detection}
              onChange={(detection) => setDraft({ ...draft, detection })}
              processNamesHint={t('catalogue.apps.sheet.processNamesHint')}
            />
          </>
        ) : null}

        {current === 'players' ? (
          <ProvisioningFields
            draft={draft.provisioning}
            onChange={(provisioning) => setDraft({ ...draft, provisioning })}
            signOut={draft.signOut}
            onSignOutChange={(signOut) => setDraft({ ...draft, signOut })}
          />
        ) : null}

        {current === 'icon' ? (
          <>
            <p className="console-muted">{t('catalogue.apps.sheet.iconIntro', { size: cap })}</p>
            <UploadDrop
              label={t('catalogue.apps.sheet.iconFile')}
              title={t('catalogue.apps.sheet.iconDrop')}
              hint={t('catalogue.apps.sheet.iconDropHint', { size: cap })}
              accept="image/png,image/webp"
              fileName={draft.icon?.name ?? null}
              onFile={(file) => {
                const problem = file === null ? undefined : iconFileProblem(file);
                if (problem === 'wrongType') setIconError(t('catalogue.apps.sheet.iconWrongType'));
                else if (problem === 'tooLarge') setIconError(t('catalogue.apps.sheet.iconTooLarge', { size: cap }));
                else setIconError(undefined);
                setDraft({ ...draft, icon: problem === undefined ? file : null });
              }}
            />
            {iconError !== undefined ? (
              <Notice tone="danger" role="alert">
                {iconError}
              </Notice>
            ) : (
              <p className="console-muted" role="status">
                {draft.icon === null ? t('catalogue.apps.sheet.iconNone') : t('catalogue.apps.sheet.iconChosen', { name: draft.icon.name })}
              </p>
            )}
          </>
        ) : null}

        {current === 'confirm' ? (
          <>
            <p className="console-muted">{t('catalogue.apps.sheet.confirmIntro')}</p>
            <dl className="console-app-summary">
              <dt>{t('catalogue.apps.sheet.name')}</dt>
              <dd>{draft.name.trim()}</dd>
              <dt>{t('catalogue.apps.sheet.category')}</dt>
              <dd>{t(`catalogue.apps.category.${draft.category}`)}</dd>
              <dt>{t('catalogue.apps.sheet.stepLaunch')}</dt>
              <dd>{draft.recipes.length === 0 ? t('catalogue.apps.sheet.noRecipes') : t('catalogue.launch.actionCount', { count: draft.recipes.length })}</dd>
              <dt>{t('catalogue.apps.sheet.processNames')}</dt>
              <dd>
                {parseProcessNames(draft.detection.processNames).length === 0
                  ? t('catalogue.apps.detectionCount', { count: 0 })
                  : parseProcessNames(draft.detection.processNames).join(', ')}
              </dd>
              <dt>{t('catalogue.apps.sheet.stepPlayers')}</dt>
              <dd>
                {t(`catalogue.apps.players.summary.${draft.provisioning.choice}`)}
                {toSignOutPaths(draft.signOut).ok && draft.signOut.some((row) => row.path.trim() !== '')
                  ? ` · ${t('catalogue.apps.players.signOutCount', { count: draft.signOut.filter((row) => row.path.trim() !== '').length })}`
                  : ''}
              </dd>
              {creating ? (
                <>
                  <dt>{t('catalogue.apps.sheet.stepIcon')}</dt>
                  <dd>{draft.icon === null ? t('catalogue.apps.sheet.iconNone') : draft.icon.name}</dd>
                </>
              ) : null}
            </dl>
          </>
        ) : null}

        <div className="console-form__actions">
          {step > 0 ? (
            <Button type="button" variant="ghost" onClick={() => setStep(step - 1)}>
              {t('catalogue.apps.sheet.back')}
            </Button>
          ) : (
            <Button type="button" variant="ghost" onClick={onDone}>
              {t('catalogue.apps.sheet.cancel')}
            </Button>
          )}
          <span style={{ flex: 1 }} />
          {current === 'confirm' ? (
            <Button type="button" variant="primary" busy={saving} onClick={() => void submit()}>
              {creating ? t('catalogue.apps.sheet.create') : t('catalogue.apps.sheet.save')}
            </Button>
          ) : (
            <Button type="button" variant="primary" onClick={next}>
              {t('catalogue.apps.sheet.next')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
